import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { EmailValidator } from 'src/app/shared/utils/emailValidator';
import { LocaleService } from 'src/app/shared/services/locale.service';
import { ContactService } from 'src/app/modules/contact/contact.service';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { ConfirmationDialogComponent } from '@theme/components';
import { LocalStorage } from '@core/localstorage';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-request-account-recover',
    templateUrl: './account-recover.component.html',
    styleUrls: ['./account-recover.component.scss'],
})
export class RequestAccountRecoverComponent implements OnInit {
    formRecover: FormGroup;
    locale: any = {
        countries: [],
        states: [],
        cities: [],
    };
    isSubmitting = false;

    get controls() {
        return this.formRecover.controls;
    }

    constructor(
        private formBuilder: FormBuilder,
        private localeService: LocaleService,
        private contactService: ContactService,
        private dialogService: NbDialogService,
        public localStorage: LocalStorage,
        private nbToastrService: NbToastrService,
        private translateService: TranslateService
    ) {}

    ngOnInit(): void {
        this.buildForm();
        this.getInitialLocale();
    }

    buildForm(): void {
        this.formRecover = this.formBuilder.group({
            fullName: ['', Validators.required],
            dateOfBirth: [null, Validators.required],
            email: ['', EmailValidator],
            cpf: ['', Validators.required],
            phone: ['', Validators.required],
            country_id: ['1', Validators.required], // Default to Brazil (id: 1)
            state_id: [null, Validators.required],
            city_id: [null, Validators.required],
            acceptDataUsage: [false, Validators.requiredTrue],
        });
    }

    async getInitialLocale() {
        try {
            const res = await this.localeService.getCountries().toPromise();
            this.locale.countries = res.registers;

            await this.onLocaleChange('country', this.formRecover.get('country_id').value);
        } catch (error) {
            console.error('Error loading initial locale data:', error);
        }
    }

    async onLocaleChange(type: string, localeId: any) {
        let res;
        switch (type) {
            case 'country':
                this.formRecover.patchValue({
                    state_id: null,
                    city_id: null,
                });
                this.locale.cities = [];
                this.locale.states = [];
                try {
                    res = await this.localeService.getStates(localeId).toPromise();
                    this.locale.states = res.registers;
                } catch (error) {
                    console.error('Error loading states:', error);
                }
                break;
            case 'state':
                this.formRecover.patchValue({
                    city_id: null,
                });
                this.locale.cities = [];
                try {
                    res = await this.localeService.getCities(localeId).toPromise();
                    this.locale.cities = res.registers;
                } catch (error) {
                    console.error('Error loading cities:', error);
                }
                break;
            default:
                break;
        }
    }

    async onRecover(): Promise<void> {
        if (this.formRecover.invalid) {
            return;
        }

        this.isSubmitting = true;
        const formData = this.formRecover.getRawValue();

        // Find selected city and state names
        const selectedCity = this.locale.cities.find((city) => city.id_cidade == formData.city_id);
        const selectedState = this.locale.states.find((state) => state.id_uf == formData.state_id);

        const recoveryData = {
            name: formData.fullName,
            birthDate: this.formatDate(formData.dateOfBirth),
            email: formData.email,
            document: formData.cpf,
            phone: formData.phone,
            city: selectedCity?.descricao || '',
            state: selectedState?.sigla || '',
        };

        try {
            const response = await this.contactService.ticketForRecoveryAccount(recoveryData).toPromise();
            console.log('Recovery request submitted successfully:', response);

            this.showSuccessDialog();
            this.clearAndDisableForm();
        } catch (error) {
            console.error('Error submitting recovery request:', error);
            this.nbToastrService.danger(
                this.translateService.instant('ALERT.ERROR.DESCRIPTION'),
                this.translateService.instant('ALERT.ERROR.TITLE')
            );
        } finally {
            this.isSubmitting = false;
        }
    }

    private formatDate(date: Date): string {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private showSuccessDialog(): void {
        const successMessage =
            'Seus dados geraram um ticket para nossa equipe de suporte, o ticket será tratado na ordem de prioridade e você receberá a resposta no email enviado. Estima-se até 72 horas para resolução do seu problema.';

        const confirmDialogRef = this.dialogService.open(ConfirmationDialogComponent, {
            context: {
                title: 'Solicitação Enviada com Sucesso',
                description: successMessage,
                buttonText: 'OK',
                status: 'success',
                confirm: () => {
                    confirmDialogRef.close();
                },
            },
        });
    }

    private clearAndDisableForm(): void {
        this.formRecover.reset({
            country_id: null,
            state_id: null,
            city_id: null,
            fullName: '',
            dateOfBirth: null,
            email: '',
            cpf: '',
            phone: '',
            acceptDataUsage: false,
        });

        this.formRecover.disable();

        this.locale.states = [];
        this.locale.cities = [];
    }
}
